import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { SchemaOf } from 'yup'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import {
  DTCAddLogoFormValues,
  DTCLogoSetupFormValues,
} from '../../../../containers/EmbroideryService/types'
import { LogoFile } from '../../../../containers/EmbroideryService/types'
import { useEmbroideryForm } from '../../../../containers/EmbroideryService/useEmbroideryForm'
import { intlPrettyCurrency } from '../../../../lib/utils'
import { createSafeContainer } from '../../../../lib/utils/safe-container'
import { LogoPreviewItem } from '../PreviewItems/LogoPreviewItem'

import { useAddLogoValidationSchema } from './useAddLogoValidationSchema'
import useEmbroideryLogoUpload from './useEmbroideryLogoUpload'
import { useLogoSetupValidationSchema } from './useLogoSetupValidationSchema'
import { normalizeDigitizationSettingToEmbroideryKind } from './utils'

type LogoFormContainerState = {
  userLogos?: LogoFile[]
  transientLogo?: LogoFile
}

const useLogoFormContainer = ({ userLogos, transientLogo }: LogoFormContainerState) => {
  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const {
    embroideryOptions,
    embroideryVariantsByKind,
    setSelectedFormKind,
    selectedFormKind,
    selectedEmbroideryOptions,
    setSelectedEmbroideryOptions,
  } = EmbroideryService.useContainer()
  const { region, locale } = useLocalization()
  const logoSetupKind = 'LOGO_SETUP'

  // Determine the initial logo files that are available for the user.
  // This should include:
  // 1. Logo files that are saved in the user's account
  // 2. Digitization requests that exist in the cart
  // 3. Digitization requests that are confirmed, but not yet added to the cart
  const initialLogoFiles = useMemo(() => {
    const out: LogoFile[] = []

    // Add user logos to available logo files
    if (userLogos && userLogos.length > 0) {
      out.push(...userLogos)
    }

    // Add existing cart digitization requests to available logo files
    if (transientLogo) out.push(transientLogo)

    // Add uploaded logo to available logo files
    if (selectedEmbroideryOptions.LOGO_SETUP?.logoFile)
      out.push(selectedEmbroideryOptions.LOGO_SETUP.logoFile)

    // De-dupe & return existing logo files
    return out.reduce<LogoFile[]>((acc, curr) => {
      if (acc.find(logo => logo.id === curr.id)) {
        return acc
      }
      return [...acc, curr]
    }, [])
  }, [userLogos, transientLogo, selectedEmbroideryOptions.LOGO_SETUP?.logoFile])

  const [logoFiles, setLogoFiles] = useState<LogoFile[]>(initialLogoFiles)

  // Handle upload of user logos
  const upload = useEmbroideryLogoUpload()

  const logoSetupPrice = useMemo(() => {
    const price =
      embroideryOptions.find(option => option.kind === 'LOGO_SETUP')?.variants[0]?.price ?? 0
    return intlPrettyCurrency({
      unit: 'cents',
      value: price,
      region: region,
      locale: locale,
      truncateTrailingZeros: true,
    })
  }, [embroideryOptions, locale, region])

  const captureError = useCallback((error: Error) => {
    reportClientError({
      error,
      context: {
        scope: 'DTCLogoEmbroideryForm useForm',
        category: 'embroidery',
        message: 'embroidery logo upload request failed',
        level: 'error',
      },
    })
  }, [])

  /* Manage local form state for the LOGO_SETUP form */
  const logoSetupValidationSchema = useLogoSetupValidationSchema({ textMaxChars: 50 })
  const logoSetupForm = useEmbroideryForm('LOGO_SETUP', {
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: logoSetupValidationSchema as SchemaOf<Partial<DTCLogoSetupFormValues>>,
    finalFormKind: 'ADD_LOGO',
    async createEmbroideryOptions(values, helpers) {
      try {
        const existingLogoSetup = selectedEmbroideryOptions.LOGO_SETUP

        // Avoid uploading the same file during editing
        let data: LogoFile
        if (
          existingLogoSetup &&
          values.file === selectedEmbroideryOptions.LOGO_SETUP?.userSelectedFile
        ) {
          data = {
            ...selectedEmbroideryOptions.LOGO_SETUP?.logoFile!,
            digitizationSetting: values.digitization_setting!,
            name: values.name ?? '',
            notes: values.notes ?? '',
          }
        } else {
          data = await upload(values)
        }

        // Update existing logo file if it exists, otherwise push new logo file
        setLogoFiles(lf => {
          if (existingLogoSetup) {
            return lf.map(logo => (logo.id === existingLogoSetup.logoFile?.id ? data : logo))
          }
          return [...lf, data]
        })

        return {
          userSelectedFile: values.file,
          setupFile: true,
          logoFile: data,
        }
      } catch (error) {
        captureError(error)
        helpers.setErrors({ file: fixtures.validationErrorMessages.fileUploadError })
        throw error
      }
    },
  })

  /* Manage local form state for the ADD_LOGO form */
  const addLogoForm = useEmbroideryForm('ADD_LOGO', {
    enableReinitialize: true,
    validationSchema: useAddLogoValidationSchema() as unknown as SchemaOf<
      Partial<DTCAddLogoFormValues>
    >,
    // Ensure that the proper logo file is initially selected based on the existing form state
    createInitialValues: initialValues => {
      const approvedLogo = logoFiles.find(lf => lf.status === 'APPROVED')
      const setupLogo =
        logoFiles.find(lf => lf.id === selectedEmbroideryOptions.LOGO_SETUP?.logoFile?.id) ??
        transientLogo
      const selectedLogo = selectedEmbroideryOptions?.ADD_LOGO?.logoFile

      const initialSelectedLogoFile = setupLogo ?? selectedLogo ?? approvedLogo

      return {
        ...initialValues,
        logoFile: initialSelectedLogoFile,
        logoFileId: initialSelectedLogoFile?.id,
      }
    },
    createEmbroideryOptions(values) {
      const colorOptionKind = normalizeDigitizationSettingToEmbroideryKind(
        values.logoFile?.digitizationSetting
      )

      const embroideryItemVariant = colorOptionKind
        ? embroideryVariantsByKind?.[colorOptionKind]
        : undefined

      const logoFile = values.logoFile ?? selectedEmbroideryOptions.LOGO_SETUP?.logoFile

      const embroiderySetupVariant =
        logoFile?.status !== 'APPROVED' ? embroideryVariantsByKind?.[logoSetupKind] : undefined

      return {
        embroideryItemVariant,
        embroiderySetupVariant,
        logoPlacement: values.logoPlacement?.name,
        logoFile: logoFile,
        logoId: logoFile?.id,
        logoPreview: logoFile?.preview,
        logoColor: logoFile?.digitizationSetting,
        logoNote: logoFile?.notes,
        logoName: logoFile?.name,
      }
    },
    previewItem: () => <LogoPreviewItem values={addLogoForm.form.values} />,
    onSubmitCompleted: values => {
      // Clear LOGO_SETUP on logo selection change to avoid duplicate fees.
      setSelectedEmbroideryOptions(prev => ({
        ...prev,
        ...(selectedFormKind === 'ADD_LOGO' && prev.LOGO_SETUP?.logoFile?.id !== values.logoFileId
          ? { LOGO_SETUP: null }
          : {}),
      }))
    },
  })

  // Price of the selected logo color SKU
  const logoColorPrice = useMemo(() => {
    const colorOption = normalizeDigitizationSettingToEmbroideryKind(
      addLogoForm.form.values.logoFile?.digitizationSetting
    )
    if (colorOption) {
      return embroideryVariantsByKind?.[colorOption]?.price ?? 0
    }
    return 0
  }, [embroideryVariantsByKind, addLogoForm.form.values.logoFile?.digitizationSetting])

  // Total price of the selected logo (color + setup)
  const logoTotalPrice = useMemo(() => {
    let price = 0
    if (
      selectedEmbroideryOptions.LOGO_SETUP?.logoFile?.id === addLogoForm.form.values.logoFile?.id
    ) {
      price += embroideryVariantsByKind?.LOGO_SETUP?.price ?? 0
    }
    price += logoColorPrice
    return price
  }, [
    selectedEmbroideryOptions,
    embroideryVariantsByKind,
    addLogoForm.form.values.logoFile?.id,
    logoColorPrice,
  ])

  const resetLogoForms = useCallback(() => {
    // Reset local & remote form states for LOGO_SETUP & ADD_LOGO
    addLogoForm.resetForm()
    logoSetupForm.resetForm()

    // Only remove logo files that are not saved or in the cart
    setLogoFiles(logoFiles =>
      logoFiles.filter(logo => {
        const isSaved = userLogos?.find(savedLogo => logo.id === savedLogo?.id)
        const isTransient = logo.id === transientLogo?.id
        return isSaved || isTransient
      })
    )
  }, [addLogoForm, logoSetupForm, transientLogo, userLogos])

  // Ensure that the correct form kind is being displayed based on existing form state & available logos on mount
  // @TODO: Revisit this to avoid having to handle the initial form view after first render
  useLayoutEffect(() => {
    setSelectedFormKind(() => {
      if (selectedEmbroideryOptions.ADD_LOGO?.logoFile) {
        return 'ADD_LOGO'
      }
      if ((userLogos && userLogos?.length > 0) || transientLogo) {
        return 'ADD_LOGO'
      }
      return 'LOGO_SETUP'
    })
    //eslint-disable-next-line
  }, [])

  return {
    logoSetupPrice,
    logoTotalPrice,
    logoColorPrice,
    logoSetupForm: logoSetupForm.form,
    addLogoForm: addLogoForm.form,
    logoFiles,
    displayLogoSetup: selectedFormKind === 'LOGO_SETUP',
    hasActiveLogoSetup: Boolean(selectedEmbroideryOptions.LOGO_SETUP),
    resetLogoForms,
    transientLogo,
    setToggleDisplay: (display: boolean) =>
      setSelectedFormKind(display ? 'LOGO_SETUP' : 'ADD_LOGO'),
  }
}

export const LogoFormContainer = createSafeContainer(useLogoFormContainer)
